/**
 * useVisualViewport
 * ─────────────────
 * Tracks iOS Safari's visual viewport (the area ABOVE the on-screen keyboard)
 * and writes two CSS custom properties on :root so CSS can react without a
 * per-page re-render:
 *
 *   --viewport-h : visualViewport.height in px — replacement for 100vh
 *                  on surfaces that must ignore the keyboard. Prefer 100dvh
 *                  in pure CSS; use --viewport-h only when you also need
 *                  --kb-offset tied to the same measurement tick.
 *
 *   --kb-offset  : px the software keyboard is covering. 0 on desktop, 0 on
 *                  Android with the default viewport behavior, non-zero on
 *                  iOS Safari while a text input is focused.
 *
 * Mount ONCE at app root. Safe on all browsers — if `window.visualViewport`
 * is missing (ancient WebKit, non-browser environments) the hook is a no-op
 * and the CSS vars simply stay at their CSS-declared defaults.
 *
 * The event listeners are `passive` implicitly (visualViewport events have
 * no cancelable default). We also re-measure on orientationchange because
 * iOS sometimes fires the resize before the rotation settles.
 */
import { useEffect } from 'react';

export function useVisualViewport() {
    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return;

        const root = document.documentElement;

        const update = () => {
            const layoutH = window.innerHeight;
            const visualH = vv.height;
            // offsetTop > 0 when the keyboard pushes the page UP (iOS behavior
            // inside <input type="text"> within a scrollable ancestor). We
            // subtract it so --kb-offset is always "amount obscured at the bottom".
            const kb = Math.max(0, layoutH - visualH - vv.offsetTop);
            root.style.setProperty('--viewport-h', `${visualH}px`);
            root.style.setProperty('--kb-offset', `${kb}px`);
        };

        update();
        vv.addEventListener('resize', update);
        vv.addEventListener('scroll', update);
        window.addEventListener('orientationchange', update);

        return () => {
            vv.removeEventListener('resize', update);
            vv.removeEventListener('scroll', update);
            window.removeEventListener('orientationchange', update);
        };
    }, []);
}
